import { NavLink } from 'react-router-dom';
import { Box, Drawer as MuiDrawer, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { common } from '@mui/material/colors';
import { CSSObject, styled, Theme } from '@mui/material/styles';

const drawerWidth = 200;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  backgroundColor: '#ffffff',
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),

  backgroundColor: '#ffffff',
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  zIndex: 999,
  boxSizing: 'border-box',
  '& .MuiPaper-root': {
    justifyContent: 'space-between',
  },
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

export const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export const StyledListItemButton = styled(ListItemButton)(() => ({
  color: common.white,
  borderRadius: '4px',
  transition: 'all 0.2s ease-in-out',
  flexDirection: 'column',
  alignItems: 'flex-start',

  '&.Mui-selected, &.Mui-selected:hover': {
    color: common.white,
    '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
      color: 'inherit',
    },
  },
  '& path': {
    fill: '#000000',
  },
  '&:hover': {
    color: common.white,
    '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
      color: common.white,
    },
    '& path': {
      fill: common.white,
    },
  },
}));

export const CollapseContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  color: '#fff',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 400,
  padding: theme.spacing(0, 1),
}));

export const StyledDrawer = styled(MuiDrawer)(() => ({
  justifyContent: 'space-between',
  borderRight: '1px solid #C0C7CA99',
}));

export const StyledCollapseTypography = styled(Typography)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 400,
  color: '#000000'
}));

export const DrawerBottom = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

export const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '52px',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

export const MenuList = styled(List)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

export const ListItemTextStyled = styled(ListItemText)(() => ({
  margin: '0px',
  padding: '0px',
}));

export const ListItemCloseIcon = styled(ListItemIcon)(() => ({
  justifyContent: 'center',
  minWidth: '30px',
}));

export const ListItemOpenIcon = styled(ListItemIcon)(() => ({
  alignItems: 'center',
  gap: '16px',
  textDecoration: 'none',
  color: '#fff',
}));

const linkStyles = `
  display: flex;
  align-items: center;
  position: relative;
  text-decoration: none;

  &.active path {
    fill: #fff;
  }
  &.active::before {
    content: '';
    position: absolute;
    top: 0;
    left: -3px;
    width: 4px;
    height: 100%;
    background-color: #16A085;
    border-radius: 0 4px 4px 0;
  }
  & path {
    fill: 'C0C7CA';
  }
  & svg {
    width: 24px;
  }
`;

export const NavigationLink = styled(NavLink)(linkStyles);
